import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { X } from 'react-feather';
import NRDragHandleIcon from '../../NRIcons/NRDragHandleIcon';
import useLocalStorageService from '../../services/local';

const MIN_WIDTH_PERCENT = 30;
const MAX_WIDTH_PERCENT = 100;

// Utility function to parse width value (percentage or pixels)
const parseWidthValue = (value) => {
  if (typeof value === 'number') {
    return { value, unit: 'px' };
  }
  if (typeof value === 'string') {
    if (value.endsWith('%')) {
      return { value: parseFloat(value), unit: '%' };
    }
    if (value.endsWith('px')) {
      return { value: parseFloat(value), unit: 'px' };
    }
    // If no unit specified, assume pixels
    return { value: parseFloat(value) || 0, unit: 'px' };
  }
  return null;
};

// Utility function to convert width to CSS string
const widthToCss = (parsed) => {
  if (!parsed) return undefined;
  return `${parsed.value}${parsed.unit}`;
};

// Utility function to convert pixels to percentage
const pixelsToPercent = (pixels, windowWidth) => {
  return (pixels / windowWidth) * 100;
};

function NRResizableDrawer({
  open,
  onClose,
  title,
  titleIcon,
  children,
  width,
  minWidth = '40%',
  maxWidth,
  contentPadding,
  storageKey
}) {
  const theme = useTheme();
  const { getStorageKeyData, setStorageKeyData } = useLocalStorageService();
  
  // Parse width props - memoize to avoid recalculation
  const parsedWidth = useMemo(() => parseWidthValue(width), [width]);
  const parsedMinWidth = useMemo(() => parseWidthValue(minWidth), [minWidth]);
  const parsedMaxWidth = useMemo(() => parseWidthValue(maxWidth), [maxWidth]);
  
  // Initialize drawerWidth based on storage, width prop, or default
  const [drawerWidth, setDrawerWidth] = useState(() => {
    // Priority 1: storage (if storageKey is provided)
    if (storageKey) {
      const storedData = getStorageKeyData(storageKey);
      if (storedData?.width != null && typeof storedData.width === 'number') {
        return storedData.width;
      }
    }
    
    // Priority 2: width prop
    const initialParsedWidth = parseWidthValue(width);
    if (initialParsedWidth) {
      if (initialParsedWidth.unit === '%') {
        return initialParsedWidth.value;
      } else {
        const windowWidth = window.innerWidth;
        return pixelsToPercent(initialParsedWidth.value, windowWidth);
      }
    }
    
    // Priority 3: default
    return 50;
  });
  
  const [isResizing, setIsResizing] = useState(false);
  const drawerRef = useRef(null);
  const previousIsResizingRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const lastWidthPropRef = useRef(width);
  const userHasResizedRef = useRef(false);
  
  // Update drawerWidth when width prop changes (but not on initial mount or after user has resized)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      lastWidthPropRef.current = width;
      return;
    }
    
    // Only update if width prop actually changed
    if (lastWidthPropRef.current === width) {
      return;
    }
    
    lastWidthPropRef.current = width;
    
    // Don't update if user has manually resized (unless width prop explicitly changes)
    if (userHasResizedRef.current) {
      // Reset the flag if width prop is explicitly changed
      userHasResizedRef.current = false;
    }
    
    // Don't update during active resizing
    if (isResizing) return;
    
    if (parsedWidth) {
      const newWidth = parsedWidth.unit === '%' 
        ? parsedWidth.value 
        : pixelsToPercent(parsedWidth.value, window.innerWidth);
      setDrawerWidth(newWidth);
      
      // Save to storage if storageKey is provided
      if (storageKey) {
        setStorageKeyData(storageKey, {
          key: 'width',
          value: newWidth
        });
      }
    }
  }, [width, storageKey, setStorageKeyData, parsedWidth]); // Removed isResizing from deps

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    e => {
      if (!isResizing) return;

      const windowWidth = window.innerWidth;
      const newWidthPercent = ((windowWidth - e.clientX) / windowWidth) * 100;
      
      // Calculate min and max constraints in percentage
      let minWidthPercent = MIN_WIDTH_PERCENT;
      let maxWidthPercent = MAX_WIDTH_PERCENT;
      
      if (parsedMinWidth) {
        if (parsedMinWidth.unit === '%') {
          minWidthPercent = parsedMinWidth.value;
        } else {
          minWidthPercent = pixelsToPercent(parsedMinWidth.value, windowWidth);
        }
      }
      
      if (parsedMaxWidth) {
        if (parsedMaxWidth.unit === '%') {
          maxWidthPercent = parsedMaxWidth.value;
        } else {
          maxWidthPercent = pixelsToPercent(parsedMaxWidth.value, windowWidth);
        }
      }

      if (newWidthPercent >= minWidthPercent && newWidthPercent <= maxWidthPercent) {
        setDrawerWidth(newWidthPercent);
        userHasResizedRef.current = true; // Mark that user has manually resized
      }
    },
    [isResizing, parsedMinWidth, parsedMaxWidth]
  );

  // Keyboard support for resizing via the handle (left/right arrows)
  const handleResizeHandleKeyDown = useCallback(
    (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();

      const windowWidth = window.innerWidth;
      const step = 2; // percentage points per key press

      let minWidthPercent = MIN_WIDTH_PERCENT;
      let maxWidthPercent = MAX_WIDTH_PERCENT;

      if (parsedMinWidth) {
        if (parsedMinWidth.unit === '%') {
          minWidthPercent = parsedMinWidth.value;
        } else {
          minWidthPercent = pixelsToPercent(parsedMinWidth.value, windowWidth);
        }
      }

      if (parsedMaxWidth) {
        if (parsedMaxWidth.unit === '%') {
          maxWidthPercent = parsedMaxWidth.value;
        } else {
          maxWidthPercent = pixelsToPercent(parsedMaxWidth.value, windowWidth);
        }
      }

      const delta = event.key === 'ArrowRight' ? -step : step;
      const proposedWidth = drawerWidth + delta;
      const newWidthPercent = Math.min(Math.max(proposedWidth, minWidthPercent), maxWidthPercent);

      setDrawerWidth(newWidthPercent);
      userHasResizedRef.current = true;
    },
    [drawerWidth, parsedMinWidth, parsedMaxWidth]
  );
  
  // Save to storage when resizing ends (transition from true to false)
  useEffect(() => {
    if (storageKey && previousIsResizingRef.current && !isResizing) {
      // Only save when resizing actually ends - use a ref to get the current value
      const currentWidth = drawerWidth;
      setStorageKeyData(storageKey, {
        key: 'width',
        value: currentWidth
      });
    }
    previousIsResizingRef.current = isResizing;
  }, [isResizing, storageKey, setStorageKeyData, drawerWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    // Save to storage immediately when resizing ends
    if (storageKey) {
      setStorageKeyData(storageKey, {
        key: 'width',
        value: drawerWidth
      });
    }
  }, [storageKey, setStorageKeyData, drawerWidth]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Set cursor to col-resize on the entire document during resize
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none'; // Prevent text selection during resize
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Reset cursor and user-select when not resizing
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Cleanup: reset cursor and user-select
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Determine the width to use - always use drawerWidth state (which is in percentage)
  // This allows resizing to work regardless of whether width prop was provided
  const getWidthStyle = () => {
    return `${drawerWidth}%`;
  };
  
  // Build PaperProps sx with proper width constraints
  const paperSx = {
    width: getWidthStyle(),
    overflow: 'visible'
  };
  
  // Apply minWidth if provided
  if (parsedMinWidth) {
    paperSx.minWidth = widthToCss(parsedMinWidth);
  }
  
  // Apply maxWidth if provided, but also respect the theme spacing constraint
  if (parsedMaxWidth) {
    const maxWidthCss = widthToCss(parsedMaxWidth);
    // Use the more restrictive of the two
    paperSx.maxWidth = maxWidthCss;
  } else {
    paperSx.maxWidth = `calc(100% - ${theme.spacing(6.5)})`;
  }

  return (
    <Drawer
      ref={drawerRef}
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: paperSx
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          onClose();
        }
      }}
    >
      {/* Resize handle - clickable area */}
      <IconButton
        onMouseDown={handleMouseDown}
        onKeyDown={handleResizeHandleKeyDown}
        aria-label="Resize drawer"
        sx={{
          position: 'absolute',
          left: theme.spacing(-4.125), // -33px (24px button width + 9px spacing)
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: isResizing ? 'col-resize' : 'pointer',
          pointerEvents: 'auto',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.spacing(0.5), // 4px
          background: theme.palette.resizableDrawer.handleBackground,
          '&:hover': {
            background: theme.palette.resizableDrawer.hoverBackground
          },
          '&:active': {
            background: theme.palette.resizableDrawer.activeBackground
          },
          padding: theme.spacing(0.75), // 6px
          width: theme.spacing(3), // 24px
          height: theme.spacing(3) // 24px
        }}
      >
        <NRDragHandleIcon color={theme.palette.text.iconAlt} />
      </IconButton>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${theme.spacing(2.25)} ${theme.spacing(3)}`, // 18px 24px
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.default'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flex: '1 0 0' }}>
            {titleIcon && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {titleIcon}
              </Box>
            )}
            <Typography variant="h3" component="div">
              {title}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ marginLeft: theme.spacing(2) }}>
            <X fontSize={16} />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.default',
            padding: contentPadding !== undefined ? contentPadding : theme.spacing(3),
            gap: theme.spacing(3) // 24px gap between rows
          }}
        >
          {children}
        </Box>
      </Box>
    </Drawer>
  );
}

export default NRResizableDrawer;
